import React from 'react';
// biome-ignore lint/performance/noNamespaceImport: intentional
import * as Sentry from '@sentry/tanstackstart-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';
import { createStart } from '@tanstack/react-start';

import { routeTree } from '@app/routeTree.gen';
import { queryClient } from '@shared/api/query-client';

createStart(() => ({
  defaultSsr: false,
  requestMiddleware: [Sentry.sentryGlobalRequestMiddleware],
  functionMiddleware: [Sentry.sentryGlobalFunctionMiddleware],
}));

export const getRouter = () => {
  const router = createRouter({
    routeTree,
    notFoundMode: 'root',
    context: { queryClient },
    scrollRestoration: true,
    // Theme providers moved into the document body (`__root.tsx` shell); a
    // theme provider rendered here, outside `<html>`, makes MantineProvider's
    // CssVariables `<style>` a sibling of `<html>` which React 19 rejects.
    Wrap: ({ children }) => (
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    ),
  });

  if (import.meta.env.PROD && !router.isServer) {
    Sentry.init({
      dsn: import.meta.env.SENTRY_DSN,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      denyUrls: ['https://track.gogocpa.app/', '\\bgogocpa\\b'],
      sendDefaultPii: true,
      enableLogs: true,
      integrations: [
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        Sentry.replayIntegration(),
        Sentry.consoleLoggingIntegration({ levels: ['error'] }),
      ],
    });
  }

  return router;
};

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
